import React, { useRef } from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import RTCModel from '@/models/rtc';
import Style from './index.less';
import { Icon, Tooltip } from '@/components';
import Tip from '@/pages/Ready/component/Tip';
import MICSettingPanel from '../../SettingPanel/MICSettingPanel';
import VideoSettingPanel from '../../SettingPanel/VideoSettingPanel';
import { off } from '@/utils/Bridge/QTBridge';

interface DevicePanelProps {
  mustCheck: boolean;
  checkMIC: () => Promise<void>;
  onMicSwitchChange: (value: boolean) => Promise<void>;
  RTCInstance?: RTCModel;
}

const DevicePanel: React.FC<DevicePanelProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer((props) => {
    const { mustCheck, checkMIC, onMicSwitchChange, RTCInstance } = props;
    const containerRef: any = useRef();
    const closedRef: any = useRef();
    const heightRef: any = useRef(0);
    const [isMICOptionVisible, setIsMICOptionVisible] = React.useState<boolean>(false);
    const [isVideoOptionVisible, setIsVideoOptionVisible] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();

    const setVideoSize = React.useCallback(
      (cut = true) => {
        if (RTCInstance && RTCInstance.localStream && containerRef.current) {
          RTCInstance.localStream.setLocalRenderMode({
            width: containerRef.current.offsetWidth,
            height: heightRef.current ? heightRef.current : containerRef.current.offsetHeight,
            cut, // 是否裁剪
          });
        }
      },
      [RTCInstance, RTCInstance?.localStream, containerRef]
    );

    const onVideoSwitchChange = React.useCallback(
      async (value: boolean) => {
        if (value) {
          if (closedRef.current) {
            heightRef.current = closedRef.current.offsetHeight;
          }
          await RTCInstance?.openCamera(false);
          await RTCInstance?.playLocalVideoStream(containerRef.current);

          // 设置播放的视频容器大小
          setVideoSize();
        } else {
          await RTCInstance?.closeCamera();
          if (containerRef.current?.children?.[0]) {
            containerRef.current.innerHTML = '';
          }
        }
      },
      [RTCInstance, RTCInstance?.localStream, containerRef]
    );

    React.useEffect(() => {
      const resizeHandle = () => {
        // 设置播放的视频容器大小
        setVideoSize();
      };
      window.addEventListener('resize', resizeHandle);
      return () => {
        window.removeEventListener('resize', resizeHandle);
        if (RTCInstance && RTCInstance.localStream) {
          RTCInstance?.localStream.stop('video');
        }
      };
    }, []);

    React.useEffect(() => {
      const cb = () => {
        setIsMICOptionVisible(false);
        setIsVideoOptionVisible(false);
      };
      document.addEventListener('click', cb);
      RTCInstance?.getDeviceOptions(false).catch((e) => console.error(e.message));
      return () => {
        document.removeEventListener('click', cb);
      };
    }, []);

    return (
      <div className={Style.panel}>
        <div
          ref={containerRef}
          className={Style.container}
          style={RTCInstance?.isOpenVideo ? { maxHeight: heightRef.current } : { display: 'none' }}
        />
        {!RTCInstance?.isOpenVideo && (
          <div className={Style['closed-video']} ref={closedRef}>
            <Icon className={Style['dark-icon']} name="icon-video_noequipment" size={32} />
            <span className={Style['closed-video-tips']}>
              {formatMessage({ id: 'CAMERA_IS_OFF' })}
            </span>
          </div>
        )}
        <div className={Style.control}>
          <div className={Style.device}>
            <div className={Style.mic}>
              <div>
                {RTCInstance?.micIsNotAvailable ? (
                  <Tooltip
                    title={formatMessage({ id: 'availableMicrophoneIsNotDetected' })}
                    className={Style.tooltip}
                    align={{ offset: [16, 0] }}
                    placement="bottomLeft"
                  >
                    <Icon
                      className={Style.unableIcon}
                      name="icon-microphone_err_1"
                      size={28}
                      mode="symbol"
                    ></Icon>
                  </Tooltip>
                ) : RTCInstance?.isOpenAudio ? (
                  <Icon
                    onClick={() => onMicSwitchChange(false)}
                    className={Style.icon}
                    name="icon-microphone_on_1"
                    size={28}
                    mode="symbol"
                  />
                ) : (
                  <Icon
                    onClick={() => onMicSwitchChange(true)}
                    className={Style.icon}
                    name="icon-microphone_off_1"
                    mode="symbol"
                    size={28}
                  />
                )}
                {!RTCInstance?.micIsNotAvailable && (
                  <div className={Style.deviceNameBox}>
                    <Icon
                      onClick={(e) => {
                        e.nativeEvent.stopImmediatePropagation();
                        setIsMICOptionVisible(!isMICOptionVisible);
                        setIsVideoOptionVisible(false);
                      }}
                      name="icon-up_2"
                      className={Style.iconRight}
                      mode="symbol"
                      size={6}
                    />
                  </div>
                )}
              </div>
              <MICSettingPanel visible={isMICOptionVisible} />
            </div>
            <div className={Style.line} />
            <div className={Style.video}>
              <div>
                {RTCInstance?.cameraIsNotAvailable ? (
                  <Tooltip
                    title={formatMessage({ id: 'unableToDetectCamera' })}
                    className={Style.tooltip}
                    align={{ offset: [16, 0] }}
                    placement="bottomLeft"
                  >
                    <Icon
                      className={Style.unableIcon}
                      name="icon-camera_err_1"
                      size={28}
                      mode="symbol"
                    />
                  </Tooltip>
                ) : (
                  <span className={Style.videoIconBox}>
                    <Tip
                      visible={!RTCInstance?.isOpenVideo}
                      content={formatMessage({ id: 'INTERVIEW_CAMERA_TIPS' })}
                      title={formatMessage({ id: 'CAMERA' })}
                      isMobile={window.isMobile}
                    />
                    {RTCInstance?.isOpenVideo ? (
                      <Icon
                        className={Style.icon}
                        name="icon-camera_on_1"
                        mode="symbol"
                        size={28}
                        onClick={() => onVideoSwitchChange(false)}
                      />
                    ) : (
                      <Icon
                        className={Style.icon}
                        name="icon-camera_off_1"
                        mode="symbol"
                        size={28}
                        onClick={() => onVideoSwitchChange(true)}
                      />
                    )}
                  </span>
                )}
                {!RTCInstance?.cameraIsNotAvailable && (
                  <div className={Style.deviceNameBox}>
                    <Icon
                      onClick={(e) => {
                        e.nativeEvent.stopImmediatePropagation();
                        setIsVideoOptionVisible(!isVideoOptionVisible);
                        setIsMICOptionVisible(false);
                      }}
                      className={Style.iconRight}
                      name="icon-up_2"
                      size={6}
                      mode="symbol"
                    />
                  </div>
                )}
              </div>
              <VideoSettingPanel visible={isVideoOptionVisible} />
            </div>
          </div>
          {!mustCheck && (
            <div className={Style.devicecheck} onClick={checkMIC}>
              <span>{formatMessage({ id: 'CHECK_DEVICES' })}</span>
            </div>
          )}
        </div>
      </div>
    );
  })
);

export default DevicePanel;
